import { useState, useEffect } from "react";
import { getCurrentUser, editProfile } from "../../services/UserService.ts";
import { getClassroom } from "../../services/ClassroomService.ts";
import "../../css/ProfilePage.css"; // Adjust as needed

const Loader = () => <div className="loader">Loading...</div>;
const ErrorMessage = ({ message }) => (
  <div className="error-message" role="alert">{message}</div>
);

const ProfileForm = ({ user, onSave, loading, success }) => {
  const [form, setForm] = useState(user);
  const [errors, setErrors] = useState({});

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const errs = {};
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) errs.email = "Valid email required";
    if (!form.password || form.password.length < 6) errs.password = "Password min 6 chars";
    return errs;
  };

  const handleSubmit = e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
    } else {
      setErrors({});
      onSave(form);
    }
  };

  return (
    <form className="profile-form" onSubmit={handleSubmit} noValidate>
      <div className="profile-field">
        <label>Email</label>
        <input
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder="Your email"
          required
        />
        {errors.email && <span className="error">{errors.email}</span>}
      </div>
      <div className="profile-field">
        <label>Password</label>
        <input
          name="password"
          type="password"
          onChange={handleChange}
          placeholder="Your new password"
          required
        />
        {success ? (
          <span className="success" style={{ color: "green" }}>Profile updated successfully</span>
        ) : (
          errors.password && <span className="error">{errors.password}</span>
        )}
      </div>
      {form.inClassroom && form.classroom && (
        <div className="profile-field">
          <label>Classroom</label>
          <input value={form.classroom.name} disabled />
        </div>
      )}
      <div className="profile-form-actions">
        <button className="save-btn" type="submit" disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
};

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        try {
          const classroom = await getClassroom();
          setUser({ ...currentUser, classroom, inClassroom: true });
        } catch (classroomError) {
          // No classroom or error fetching it - set user without classroom
          setUser(currentUser);
        }
      } catch (e) {
        setError("Could not load profile right now. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleSave = async updatedUser => {
    setSaveLoading(true);

    try {
      const savedUser = await editProfile(updatedUser.email, updatedUser.password);
      setUser(savedUser);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError("Failed to save profile. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <ErrorMessage message={error} />;
  if (!user) return <div>No user data found.</div>;

  return (
    <main className="profile-page">
      <header>
        <h1>Profile</h1>
      </header>
      <ProfileForm user={user} onSave={handleSave} loading={saveLoading} success={success} />
    </main>
  );
};

export default ProfilePage;